// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CustomAppState, XProductInfo } from "@werner/App/Store/Reducers";
import cloneDeep from "lodash/cloneDeep";

type Parameter = {
    productInfo: ProductInfo;
    product: ProductModel;
    index: number;
};

type Props = {
    productInfo: XProductInfo;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginAddProduct: HandlerType = props => {
    props.dispatch({
        type: "xMiniQuickOrder/QuickOrder/BeginAddProduct",
    } as any);
};

export const CopyCurrentValues: HandlerType = props => {
    props.productInfo = { ...cloneDeep(props.parameter.productInfo), index: props.parameter.index };
};

export const GetPrice: HandlerType = async props => {
    const { productId, unitOfMeasure, qtyOrdered } = props.productInfo;

    if (props.parameter.product.quoteRequired) {
        return;
    }

    let loadedPricing = false;

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [{ productId, unitOfMeasure, qtyOrdered }],
            onComplete: realTimePricingProps => {
                if (realTimePricingProps.apiResult) {
                    props.productInfo.pricing = realTimePricingProps.apiResult.realTimePricingResults?.find(
                        o => o.productId === productId,
                    );
                } else if (realTimePricingProps.error) {
                    props.productInfo.failedToLoadPricing = true;
                }

                loadedPricing = true;
            },
        }),
    );

    await waitFor(() => loadedPricing);
};

export const GetInventory: HandlerType = async props => {
    const { productId } = props.productInfo;

    let loadedInventory = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds: [productId],
            onComplete: realTimeInventoryProps => {
                loadedInventory = true;
                if (realTimeInventoryProps.error) {
                    if (props.productInfo) {
                        props.productInfo.failedToLoadInventory = true;
                    }
                } else {
                    props.productInfo.inventory = realTimeInventoryProps?.apiResult?.realTimeInventoryResults?.find(
                        o => o.productId === productId,
                    );
                }
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const DispatchCompleteAddProduct: HandlerType = props => {
    props.dispatch({
        type: "xMiniQuickOrder/QuickOrder/CompleteAddProduct",
        productInfo: props.productInfo,
    } as any);
};

export const chain = [DispatchBeginAddProduct, CopyCurrentValues, GetPrice, GetInventory, DispatchCompleteAddProduct];

const addProduct = createHandlerChainRunner(chain, "AddMiniQuickOrderProduct");
export default addProduct;
